import { COST_UNIT } from "../../Units";
import { normalizeDisplayLabel } from "../../utility/displayText";
import { LegendSummaryTableProps } from "./types";
import { calculateLegendStats } from "./calculateLegendStats";

/**
 * Downloads the summary data in CSV format.
 *
 * @param legends - Array of legend configurations.
 * @param data - Array of data objects.
 * @param unit - Unit of measurement ("mw" or "mwh"). If "mw", total is converted to MWh.
 * @param extraKeys - Array of keys to extract extra info from the data records.
 * @param extraLabels - Array of labels corresponding to the extra keys.
 */
export function downloadSummaryCSV({
  legends,
  data,
  unit = "MWh",
  tableData,
  extraKeys,
  extraLabels,
  filename = "summary.csv",
}: LegendSummaryTableProps): void {
  // Calculate summary statistics using the same logic as in LegendSummaryTable.
  const stats = calculateLegendStats(unit, legends, data, extraKeys, tableData);

  // If unit is "mw", convert the total to MWh (divide by 4); otherwise, leave as is.
  const adjustedStats = stats.map((stat) => ({
    ...stat,
  }));

  // Determine the label for the numeric values.
  const valueUnitLabel = unit === "mw" ? "MW" : unit === "mwh" ? "MWh" : "MUs";

  // Construct the CSV header.
  // Columns: Name, Min (unit), [for each extra label: Min - {label}], Max (unit), [for each extra label: Max - {label}], Avg (unit), Total (MWh)
  const headers = [
    "Name",
    `Min (${valueUnitLabel})`,
    ...extraLabels.map((label) => `Min - ${label}`),
    `Max (${valueUnitLabel})`,
    ...extraLabels.map((label) => `Max - ${label}`),
    `Avg (${valueUnitLabel})`,
    `Total (${unit === "mw" ? "MWh" : unit === "mwh" ? "MWh" : "MUs"})`,
  ];

  // Build CSV rows for each legend summary.
  const rows = adjustedStats.map((stat) => {
    const row = [];
    row.push(stat.name);
    row.push(stat.min.toFixed(2));
    // Append each extra value for the min record
    for (let i = 0; i < extraKeys.length; i++) {
      row.push(stat.minExtra[extraKeys[i]]);
    }
    row.push(stat.max.toFixed(2));
    // Append each extra value for the max record
    for (let i = 0; i < extraKeys.length; i++) {
      row.push(stat.maxExtra[extraKeys[i]]);
    }
    row.push(stat.avg.toFixed(2));
    row.push(stat.total.toFixed(2));
    return row;
  });

  // Combine headers and rows into a CSV string.
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  // Create a Blob from the CSV string and trigger a download.
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename + " Summary Data");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Downloads the summary data (displayed in ChartTable/LegendSummaryTable) as a CSV file.
 * The CSV columns are built as follows:
 *   - The first columns come from extraKeys (labeled using extraLabels)
 *   - The remaining columns come from legends (header includes the legend name and unit)
 *
 * @param legends - Array of legend configurations.
 * @param data - Array of data objects.
 * @param unit - Unit of measurement ("mw" or "mwh").
 * @param extraKeys - Keys to display as the first columns.
 * @param extraLabels - Corresponding labels for the extra keys.
 */
export function downloadChartTableCSV({
  filename = "chart_table.csv",
  legends,
  data,
  unit,
  extraKeys,
  extraLabels,
}: LegendSummaryTableProps): void {
  // Build the header row:
  // Use extraLabels for the extra columns, and for each legend add "legendName (UNIT)"

  function calculateLegends(legend: any) {
    const displayName = normalizeDisplayLabel(legend.name).trim();
    if (/\(forecast\)\s*$/i.test(displayName)) return displayName;

    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      // Otherwise check against the suffix dictionary
      const suffixDict = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      // Find if series name contains any of the keys in suffixDict
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key),
      );

      // Use either the found suffix or the default unit
      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : legend?.unit || unit || "";
    }

    // Strip any existing parenthesized unit from the name to avoid duplication
    const trailingParenMatch = displayName.match(/\(([^)]*)\)\s*$/);
    const trailingParenValue = trailingParenMatch?.[1]?.trim().toLowerCase();
    const suffixValue = String(suffix || "")
      .trim()
      .toLowerCase();
    const cleanName =
      trailingParenValue && suffixValue && trailingParenValue === suffixValue
        ? displayName.replace(/\s*\([^)]*\)\s*$/, "").trim()
        : displayName;
    return `${cleanName} (${suffix})`;
  }
  const headers = [
    ...extraKeys.map((key, idx) => extraLabels[idx] || key),
    ...legends.map((legend) => calculateLegends(legend)),
  ];

  // Build CSV rows from each data object.
  // For each row, first output values for extraKeys, then for each legend's dataKey.
  const formatNum = (v: any) => {
    if (v === undefined || v === null || v === "") return "";
    const n = Number(v);
    return isNaN(n) ? String(v) : n.toFixed(2);
  };

  const rows = data.map((row) => {
    const extraValues = extraKeys.map((key) => {
      const value = row[key] !== undefined ? row[key] : "";
      return typeof value === "string" && value.includes(",")
        ? `"${value}"`
        : value;
    });
    const legendValues = legends.map((legend) => {
      const raw = row[legend.dataKey];
      if (raw === undefined || raw === null) return "";
      const formatted = formatNum(raw);
      if (legend.deviationValueKey && row[legend.deviationValueKey] != null) {
        return `"${formatted} (${formatNum(row[legend.deviationValueKey])})"`;
      }
      return formatted;
    });
    return [...extraValues, ...legendValues];
  });

  // Combine header and rows into a CSV string.
  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  // Create a Blob and trigger a download.
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename + " Chart Data");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
